import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { subdomainFormatMessage } from '@/lib/tenant-identity';

import { checkSubdomainAvailability } from '../api/tenants.graphql';

export const SUBDOMAIN_DEBOUNCE_MS = 400;

export type SubdomainStatus =
  | { state: 'empty' }
  | { state: 'checking' }
  | { state: 'available' }
  | { state: 'unavailable'; message: string }
  | { state: 'error'; message: string };

const REASON_MESSAGES: Record<string, string> = {
  RESERVED: 'Este subdominio está reservado por OpsSynk. Elige otro.',
  TAKEN: 'Este subdominio ya lo usa otro tenant. Elige otro.',
  INVALID_FORMAT: 'El subdominio no tiene un formato válido.',
};

export function subdomainQueryKey(subdomain: string, tenantId?: string): readonly unknown[] {
  return ['platform', 'subdomain-available', subdomain, tenantId ?? null];
}

/**
 * OS-1712 — consulta al API (con debounce) si el subdominio está libre y no es reservado.
 * OS-1725 C1b — al editar se pasa `tenantId` para que el propio tenant no cuente como ocupante; el
 * API rechaza también el id de otro tenant. Es orientativo: la verificación definitiva es al guardar.
 */
export function useSubdomainAvailability(subdomain: string, tenantId?: string): SubdomainStatus {
  const [debounced, setDebounced] = useState(subdomain);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(subdomain), SUBDOMAIN_DEBOUNCE_MS);
    return (): void => clearTimeout(timer);
  }, [subdomain]);

  const formatMessage = subdomainFormatMessage(subdomain);
  const query = useQuery({
    queryKey: subdomainQueryKey(debounced, tenantId),
    queryFn: () => checkSubdomainAvailability(debounced, tenantId),
    enabled: debounced !== '' && subdomainFormatMessage(debounced) === null,
    staleTime: 30_000,
    retry: false,
  });

  if (subdomain === '') {
    return { state: 'empty' };
  }
  if (formatMessage) {
    return { state: 'unavailable', message: formatMessage };
  }
  if (debounced !== subdomain || query.isFetching) {
    return { state: 'checking' };
  }
  if (query.isError) {
    return {
      state: 'error',
      message: 'No se pudo verificar el subdominio. Revisa la conexión e intenta de nuevo.',
    };
  }
  if (!query.data) {
    return { state: 'checking' };
  }
  return query.data.available
    ? { state: 'available' }
    : {
        state: 'unavailable',
        message: REASON_MESSAGES[query.data.reason ?? ''] ?? 'Este subdominio no está disponible.',
      };
}
